import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Lightning, ShieldCheck, Star, FileText, PencilSimple, ChatCircleText, LockSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PublicNav } from "@/components/layout/PublicNav";
import { AgentAvatar, Rating } from "@/components/agent/AgentCard";
import { ChatPanel } from "@/components/chat/ChatPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Icon } from "@/lib/icons";
import { api, errMsg, fmtPrice, fmtDate, startLogin } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShareBar, useTitle } from "@/components/growth";

const ReviewForm = ({ agentId, onDone }) => {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const submit = async () => {
    try {
      await api.post(`/marketplace/${agentId}/reviews`, { rating, comment });
      toast.success("Valoración publicada");
      setComment("");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel p-5" data-testid="review-form">
      <p className="label">Tu valoración</p>
      <div className="mb-3 flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => setRating(n)} data-testid={`review-star-${n}`}>
            <Star size={22} weight={n <= rating ? "fill" : "regular"} className={n <= rating ? "text-amber-400" : "text-zinc-600"} />
          </button>
        ))}
      </div>
      <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} className="field" placeholder="¿Qué tal te ha funcionado?" data-testid="review-comment-input" />
      <button onClick={submit} className="btn-primary mt-3" data-testid="review-submit-button">Publicar valoración</button>
    </div>
  );
};

export default function AgentDetail() {
  const { agentId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [buying, setBuying] = useState(false);
  const { data: agent, isLoading, isError } = useQuery({ queryKey: ["mdetail", agentId], queryFn: async () => (await api.get(`/marketplace/${agentId}`)).data });
  const { data: access, refetch: refetchAccess } = useQuery({
    queryKey: ["access", agentId, user?.user_id], enabled: !!user,
    queryFn: async () => (await api.get(`/agents/${agentId}/access`)).data,
  });

  useTitle(agent?.name);
  const buy = async () => {
    if (!user) return startLogin(`/agent/${agentId}`);
    setBuying(true);
    try {
      if (!agent.price) {
        await api.post(`/marketplace/${agentId}/acquire-free`);
        toast.success("Agente añadido a tu biblioteca");
        refetchAccess();
        qc.invalidateQueries({ queryKey: ["mdetail", agentId] });
      } else {
        const { data } = await api.post("/payments/checkout", { agent_id: agentId, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
        return;
      }
    } catch (e) { toast.error(errMsg(e)); }
    setBuying(false);
  };

  if (isLoading) return <div className="min-h-screen bg-[#050505]"><PublicNav /><div className="h-px w-full bg-white/10 thinking-beam" /></div>;
  if (isError || !agent) return (
    <div className="min-h-screen bg-[#050505] text-white"><PublicNav />
      <div className="mx-auto max-w-7xl px-6 py-24"><h1 className="font-display text-4xl font-bold">Agente no disponible</h1><Link to="/marketplace" className="btn-ghost mt-6">Volver al marketplace</Link></div>
    </div>
  );

  const role = access?.role;
  const owned = role === "owner" || role === "buyer";
  const CTA = () => {
    if (role === "owner") return <Link to={`/agents/${agentId}/edit`} className="btn-ghost w-full" data-testid="agent-edit-button"><PencilSimple size={16} /> Editar agente</Link>;
    if (role === "buyer") return <Link to={`/chat/${agentId}`} className="btn-primary w-full" data-testid="agent-open-chat-button"><ChatCircleText size={16} /> Abrir chat completo</Link>;
    return (
      <button onClick={buy} disabled={buying} className="btn-primary w-full !py-3.5 text-base" data-testid="agent-buy-button">
        {buying ? "Procesando…" : agent.price ? `Comprar · ${fmtPrice(agent)}` : "Obtener gratis"}
      </button>
    );
  };

  return (
    <div className="noise min-h-screen bg-[#050505] text-white">
      <PublicNav />
      <div className="mx-auto max-w-7xl px-6 py-10">
        <button onClick={() => navigate(-1)} className="mb-8 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white" data-testid="agent-back-button"><ArrowLeft size={16} /> Volver</button>
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="reveal flex items-start gap-5">
              <AgentAvatar icon={agent.icon} color={agent.color} size={72} />
              <div>
                <p className="eyebrow mb-2">{agent.category}</p>
                <h1 className="font-display text-4xl font-extrabold tracking-tighter sm:text-5xl" data-testid="agent-detail-name">{agent.name}</h1>
                <p className="mt-3 text-lg text-zinc-400">{agent.tagline}</p>
                <div className="mt-5"><ShareBar title={agent.name} /></div>
              </div>
            </div>
            <div className="mt-8 grid grid-cols-2 border border-white/10 sm:grid-cols-4">
              {[["Modelo", agent.model_label], ["Valoración", <Rating key="r" agent={agent} />], ["Usuarios", agent.sales_count || 0], ["Creador", agent.owner?.name]].map(([l, v]) => (
                <div key={l} className="border-b border-r border-white/10 p-4 sm:border-b-0">
                  <p className="text-[11px] uppercase tracking-widest text-zinc-500">{l}</p>
                  <div className="mt-1.5 truncate text-sm font-semibold text-white">{v}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 lg:hidden"><CTA /></div>
            <Tabs defaultValue="overview" className="mt-10">
              <TabsList className="h-auto w-full justify-start gap-6 rounded-none border-b border-white/10 bg-transparent p-0">
                {[["overview", "Descripción"], ["skills", `Skills (${agent.skills.length})`], ["reviews", `Reseñas (${agent.rating_count || 0})`]].map(([v, l]) => (
                  <TabsTrigger key={v} value={v} data-testid={`agent-tab-${v}`} className="rounded-none border-b-2 border-transparent px-0 pb-3 text-sm text-zinc-500 data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:shadow-none">{l}</TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value="overview" className="pt-6">
                <p className="whitespace-pre-line leading-relaxed text-zinc-300">{agent.description || agent.tagline}</p>
                <div className="mt-8 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2">
                  <div className="bg-[#080808] p-5"><FileText size={20} className="text-blue-500" /><p className="mt-3 text-sm font-semibold">Base de conocimiento</p><p className="mt-1 text-sm text-zinc-500">{agent.doc_count ? `${agent.doc_count} fuentes entrenadas${agent.formats.length ? ` · ${agent.formats.join(", ").toUpperCase()}` : ""}` : "Conocimiento general del modelo"}</p></div>
                  <div className="bg-[#080808] p-5"><ShieldCheck size={20} className="text-blue-500" /><p className="mt-3 text-sm font-semibold">Acceso</p><p className="mt-1 text-sm text-zinc-500">{agent.pricing_type === "monthly" ? "Suscripción mensual, cancela cuando quieras" : agent.price ? "Pago único, acceso permanente" : "Gratuito"}</p></div>
                </div>
              </TabsContent>
              <TabsContent value="skills" className="pt-6">
                <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2">
                  {agent.skills.map((s) => (
                    <div key={s.name} className="flex gap-3 bg-[#080808] p-5">
                      <Icon name={s.icon} size={20} weight="duotone" className="mt-0.5 shrink-0 text-cyan-400" />
                      <div><p className="text-sm font-semibold">{s.name}</p><p className="mt-1 text-sm text-zinc-500">{s.description}</p></div>
                    </div>
                  ))}
                  {agent.skills.length === 0 && <p className="bg-[#080808] p-5 text-sm text-zinc-500">Sin skills adicionales.</p>}
                </div>
              </TabsContent>
              <TabsContent value="reviews" className="space-y-4 pt-6">
                {role === "buyer" && <ReviewForm agentId={agentId} onDone={() => qc.invalidateQueries({ queryKey: ["mdetail", agentId] })} />}
                {agent.reviews.length === 0 && <p className="text-sm text-zinc-500" data-testid="reviews-empty">Aún no hay reseñas.</p>}
                {agent.reviews.map((r) => (
                  <div key={r.review_id} className="border-b border-white/10 pb-4">
                    <div className="flex items-center gap-2">
                      {[...Array(5)].map((_, i) => <Star key={i} size={12} weight="fill" className={i < r.rating ? "text-amber-400" : "text-zinc-700"} />)}
                      <span className="ml-2 text-sm font-semibold">{r.user_name}</span><span className="text-xs text-zinc-600">{fmtDate(r.created_at)}</span>
                    </div>
                    {r.comment && <p className="mt-2 text-sm text-zinc-400">{r.comment}</p>}
                  </div>
                ))}
              </TabsContent>
            </Tabs>
          </div>

          <div className="lg:col-span-5">
            <div className="sticky top-24 space-y-4">
              <div className="panel hidden p-6 lg:block">
                <p className="text-[11px] uppercase tracking-widest text-zinc-500">Precio</p>
                <p className="mt-1 font-mono text-3xl font-semibold" data-testid="agent-detail-price">{fmtPrice(agent)}</p>
                <div className="mt-5"><CTA /></div>
                {!owned && <p className="mt-3 text-xs text-zinc-500">Pago seguro con Stripe · Acceso inmediato</p>}
              </div>
              <div className="h-[520px] border border-white/10" data-testid="agent-trial-chat">
                {user ? (
                  <ChatPanel
                    agent={agent}
                    title={owned ? "Chat" : `Prueba gratis · ${access?.trial_remaining ?? "…"} mensajes`}
                    onMeta={() => refetchAccess()}
                    onBlocked={() => refetchAccess()}
                    disabled={role === "trial" && access?.trial_remaining === 0}
                    disabledNode={<div className="flex items-center justify-between gap-3 text-sm text-zinc-400"><span className="inline-flex items-center gap-2"><LockSimple size={16} /> Prueba agotada</span><button onClick={buy} className="btn-primary !py-2" data-testid="trial-buy-button">Desbloquear</button></div>}
                  />
                ) : (
                  <div className="flex h-full flex-col items-start justify-end gap-4 bg-[#080808] p-6">
                    <Lightning size={28} weight="duotone" className="text-cyan-400" />
                    <p className="font-display text-2xl font-bold">Pruébalo gratis</p>
                    <p className="text-sm text-zinc-500">Inicia sesión para enviar hasta 5 mensajes de prueba a este agente.</p>
                    <button onClick={() => startLogin(`/agent/${agentId}`)} className="btn-primary" data-testid="trial-login-button">Iniciar sesión con Google</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
